import { useState } from "react";
import Select from "react-select";
import Datepicker from "react-tailwindcss-datepicker";
import { SlIcon } from "@shoelace-style/shoelace/dist/react";
import { LINE_ITEM_STATUS } from "../utils/useTableState";

const FilterBar = ({
    dateRange,
    onDateChange,
    designer,
    onDesignerChange,
    designerOptions,
    showDesigner = true,
    status,
    onStatusChange,
    statusOptions,
    showStatus = true,
    adType,
    onAdTypeChange,
    adTypeOptions = [],
    showAdType = true,
    search = "",
    onSearchChange,
    showSearch = true,
    salesPerson,
    onSalesPersonChange,
    salesPersonOptions = [],
    showSalesPerson = false,
    salesLocation,
    salesLocationOptions = [],
    showSalesLocation = false,
    onSalesLocationChange,
    selectedLineItemStatus,
    onLineItemStatusChange,
    showLineItemsFiler,
    sortByLatestMessage = false,
    onSortByLatestMessageChange,
    showSortByLatestMessage = false,
}) => {
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Number of active filters (for the mobile trigger badge)
    const activeCount = [
        dateRange?.startDate || dateRange?.endDate,
        showSearch && search,
        showAdType && adType,
        showDesigner && designer,
        showSalesPerson && salesPerson,
        showSalesLocation && salesLocation?.length,
        showLineItemsFiler && selectedLineItemStatus,
        showStatus && status,
    ].filter(Boolean).length;

    const fields = (
        <>
            <div className="w-64">
                <label className="block text-xs font-semibold mb-1">Start Date Range</label>
                <div className="border rounded-lg border-gray-300 hover:cursor-pointer hover:border-2 hover:border-sky-500 w-66">
                    <Datepicker
                        value={dateRange}
                        onChange={onDateChange}
                        primaryColor="cyan"
                        showShortcuts={true}
                        classNames="form-input"
                        asSingle={false}
                        containerClassName={(defaultClassName) => `${defaultClassName} filterbar-date-container`}
                        popoverDirection="down"
                    />
                </div>
            </div>
            {showSearch && (
                <div className="w-64">
                    <label className="block text-xs font-semibold mb-1">Search</label>
                    <input type="text" className="w-full border rounded px-2 py-1" placeholder="Search by campaign/brand/client" value={search} onChange={(e) => onSearchChange && onSearchChange(e.target.value)} />
                </div>
            )}
            {showAdType && (
                <div className="w-64">
                    <label className="block text-xs font-semibold mb-1">Ad Type</label>
                    <Select
                        options={adTypeOptions}
                        value={adType}
                        onChange={onAdTypeChange}
                        isClearable
                        placeholder="Select Ad Type"
                        classNamePrefix="react-select"
                        menuPlacement="auto"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </div>
            )}
            {showDesigner && (
                <div className="w-64">
                    <label className="block text-xs font-semibold mb-1">Designer</label>
                    <Select
                        options={designerOptions}
                        value={designer}
                        onChange={onDesignerChange}
                        isClearable
                        placeholder="Select Designer"
                        classNamePrefix="react-select"
                        menuPlacement="auto"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </div>
            )}
            {showSalesPerson && (
                <div className="w-64">
                    <label className="block text-xs font-semibold mb-1">Sales Person</label>
                    <Select
                        options={salesPersonOptions}
                        value={salesPerson}
                        onChange={onSalesPersonChange}
                        isClearable
                        placeholder="Select Sales Person"
                        classNamePrefix="react-select"
                        menuPlacement="auto"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </div>
            )}
            {showSalesLocation && (
                <div className="w-64">
                    <label className="block text-xs font-semibold mb-1">Sales Location</label>
                    <Select
                        options={salesLocationOptions}
                        value={salesLocation}
                        onChange={onSalesLocationChange}
                        isClearable
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        placeholder="Select Sales Location"
                        classNamePrefix="react-select"
                        className="sales-location-select"
                        menuPlacement="auto"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            valueContainer: (base) => ({
                                ...base,
                                flexWrap: "nowrap",
                                overflowX: "auto",
                                overflowY: "hidden",
                            }),
                            multiValue: (base) => ({
                                ...base,
                                flexShrink: 0,
                            }),
                            multiValueLabel: (base) => ({
                                ...base,
                                whiteSpace: "nowrap",
                            }),
                        }}
                    />
                </div>
            )}
            {showLineItemsFiler && (
                <div className="w-64">
                    <label className="block text-xs font-semibold mb-1">Line Item Status</label>
                    <Select
                        options={LINE_ITEM_STATUS}
                        value={selectedLineItemStatus}
                        onChange={onLineItemStatusChange}
                        isClearable
                        placeholder="Select Line Item Status"
                        classNamePrefix="react-select"
                        menuPlacement="auto"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </div>
            )}
            {showStatus && (
                <div className="w-64">
                    <label className="block text-xs font-semibold mb-1">Design Status</label>
                    <Select
                        options={statusOptions}
                        value={status}
                        onChange={onStatusChange}
                        isClearable
                        placeholder="Select Status"
                        classNamePrefix="react-select"
                        menuPlacement="auto"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </div>
            )}
            {showSortByLatestMessage && (
                <div className="flex flex-col gap-1">
                    <label className="block text-xs font-semibold mb-1">Sort by Latest Message</label>
                    <label className="inline-flex items-center cursor-pointer gap-2">
                        <div
                            onClick={() => onSortByLatestMessageChange && onSortByLatestMessageChange(!sortByLatestMessage)}
                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0 ${sortByLatestMessage ? 'bg-sky-600' : 'bg-gray-300'}`}
                        >
                            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${sortByLatestMessage ? 'translate-x-5' : 'translate-x-0'}`} />
                        </div>
                        <span className="text-sm text-gray-600">{sortByLatestMessage ? 'On' : 'Off'}</span>
                    </label>
                </div>
            )}
        </>
    );

    return (
        <>
            {/* Desktop: inline filter bar */}
            <div className="flex flex-wrap gap-3 mb-4 items-end mobHide">{fields}</div>

            {/* Mobile: trigger button that opens the filters modal */}
            <div className="filterbar-mobile-trigger">
                <button type="button" className="filterbar-trigger-btn" onClick={() => setShowMobileFilters(true)}>
                    <SlIcon library="lucide" name="sliders-horizontal" style={{ fontSize: "16px" }} />
                    <span>Filters</span>
                    {activeCount > 0 && <span className="filterbar-trigger-badge">{activeCount}</span>}
                </button>
            </div>

            {/* Mobile: filters modal */}
            {showMobileFilters && (
                <div className="filterbar-modal-overlay" onClick={() => setShowMobileFilters(false)}>
                    <div className="filterbar-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="filterbar-modal__head">
                            <span>Filters</span>
                            <button type="button" className="filterbar-modal__close" onClick={() => setShowMobileFilters(false)} aria-label="Close">
                                <SlIcon library="lucide" name="x" style={{ fontSize: "20px" }} />
                            </button>
                        </div>
                        <div className="filterbar-modal__body">{fields}</div>
                        <div className="filterbar-modal__foot">
                            <button type="button" className="filterbar-apply-btn" onClick={() => setShowMobileFilters(false)}>
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FilterBar;
